import { closestCenter, DndContext, KeyboardSensor, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { arrayMove, SortableContext, sortableKeyboardCoordinates, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Edit2, Eye, EyeOff, GripVertical, Image as ImageIcon, Save, Trash2, Upload, Video, X } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import axios from '../../utils/axiosHandler';

// Sortable Gallery Item Component
const SortableGalleryItem = ({ item, editingItem, setEditingItem, spanOptions, handleUpdateItem, handleDeleteItem, toggleActive }) => {
    const {
        attributes,
        listeners,
        setNodeRef,
        transform,
        transition,
        isDragging,
    } = useSortable({ id: item._id });

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1,
        zIndex: isDragging ? 1000 : 1,
    };

    return (
        <div ref={setNodeRef} style={style} className={`p-4 hover:bg-gray-50 ${isDragging ? 'bg-blue-50 shadow-lg' : ''}`}>
            {editingItem?._id === item._id ? (
                // Edit Mode
                <div className="space-y-3">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <input
                            type="text"
                            value={editingItem.title}
                            onChange={(e) => setEditingItem({ ...editingItem, title: e.target.value })}
                            className="px-3 py-2 border border-gray-300 rounded-lg"
                            placeholder="Title"
                        />
                        <select
                            value={editingItem.span}
                            onChange={(e) => setEditingItem({ ...editingItem, span: e.target.value })}
                            className="px-3 py-2 border border-gray-300 rounded-lg"
                        >
                            {spanOptions.map(option => (
                                <option key={option.value} value={option.value}>
                                    {option.label}
                                </option>
                            ))}
                        </select>
                    </div>
                    <textarea
                        value={editingItem.description}
                        onChange={(e) => setEditingItem({ ...editingItem, description: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                        placeholder="Description"
                        rows="2"
                    />
                    <div className="flex gap-2">
                        <button
                            onClick={() => handleUpdateItem(item._id, {
                                title: editingItem.title,
                                description: editingItem.description,
                                span: editingItem.span,
                            })}
                            className="bg-green-500 text-white px-4 py-2 rounded-lg hover:bg-green-600 flex items-center gap-2"
                        >
                            <Save className="w-4 h-4" />
                            Save
                        </button>
                        <button
                            onClick={() => setEditingItem(null)}
                            className="bg-gray-500 text-white px-4 py-2 rounded-lg hover:bg-gray-600 flex items-center gap-2"
                        >
                            <X className="w-4 h-4" />
                            Cancel
                        </button>
                    </div>
                </div>
            ) : (
                // View Mode
                <div className="flex items-start gap-4">
                    {/* Drag Handle */}
                    <div
                        {...attributes}
                        {...listeners}
                        className="flex items-center justify-center w-8 h-24 cursor-grab active:cursor-grabbing text-gray-400 hover:text-gray-600"
                        title="Drag to reorder"
                    >
                        <GripVertical className="w-5 h-5" />
                    </div>

                    {/* Thumbnail */}
                    <div className="w-24 h-24 rounded-lg overflow-hidden bg-gray-100 flex-shrink-0">
                        {item.type === 'video' ? (
                            <video src={item.url} className="w-full h-full object-cover" />
                        ) : (
                            <img src={item.url} alt={item.title} className="w-full h-full object-cover" />
                        )}
                    </div>

                    {/* Content */}
                    <div className="flex-1">
                        <div className="flex items-start justify-between">
                            <div>
                                <h3 className="font-semibold text-lg flex items-center gap-2">
                                    {item.title}
                                    {item.type === 'video' ? (
                                        <Video className="w-4 h-4 text-blue-500" />
                                    ) : (
                                        <ImageIcon className="w-4 h-4 text-green-500" />
                                    )}
                                </h3>
                                <p className="text-gray-600 text-sm mt-1">{item.description}</p>
                                <div className="flex items-center gap-4 mt-2 text-xs text-gray-500">
                                    <span>Size: {spanOptions.find(s => s.value === item.span)?.label || 'Custom'}</span>
                                    <span>Order: {item.order}</span>
                                    <span className={`px-2 py-1 rounded ${item.isActive ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                                        {item.isActive ? 'Active' : 'Inactive'}
                                    </span>
                                </div>
                            </div>

                            {/* Actions */}
                            <div className="flex gap-2">
                                <button
                                    onClick={() => toggleActive(item)}
                                    className={`p-2 rounded-lg ${item.isActive ? 'bg-green-100 text-green-700 hover:bg-green-200' : 'bg-yellow-100 text-yellow-700 hover:bg-yellow-200'}`}
                                    title={item.isActive ? 'Hide' : 'Show'}
                                >
                                    {item.isActive ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                                </button>
                                <button
                                    onClick={() => setEditingItem(item)}
                                    className="p-2 bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200"
                                    title="Edit"
                                >
                                    <Edit2 className="w-4 h-4" />
                                </button>
                                <button
                                    onClick={() => handleDeleteItem(item._id)}
                                    className="p-2 bg-red-100 text-red-700 rounded-lg hover:bg-red-200"
                                    title="Delete"
                                >
                                    <Trash2 className="w-4 h-4" />
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

const GalleryManagement = () => {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [uploading, setUploading] = useState(false);
    const [editingItem, setEditingItem] = useState(null);
    const [showUploadForm, setShowUploadForm] = useState(false);
    const [savingOrder, setSavingOrder] = useState(false);
    const [previewUrl, setPreviewUrl] = useState(null);

    const [uploadForm, setUploadForm] = useState({
        title: '',
        description: '',
        span: 'md:col-span-1 md:row-span-2 sm:col-span-1 sm:row-span-2',
        file: null,
    });

    // Cleanup preview URL when file changes or component unmounts
    useEffect(() => {
        return () => {
            if (previewUrl) {
                URL.revokeObjectURL(previewUrl);
            }
        };
    }, [previewUrl]);

    const spanOptions = [
        { label: 'Small (1x2)', value: 'md:col-span-1 md:row-span-2 sm:col-span-1 sm:row-span-2' },
        { label: 'Wide (2x2)', value: 'md:col-span-2 md:row-span-2 sm:col-span-2 sm:row-span-2' },
        { label: 'Tall (1x3)', value: 'md:col-span-1 md:row-span-3 sm:col-span-1 sm:row-span-2' },
        { label: 'Large (2x3)', value: 'md:col-span-2 md:row-span-3 sm:col-span-2 sm:row-span-2' },
    ];

    // DnD sensors
    const sensors = useSensors(
        useSensor(PointerSensor, {
            activationConstraint: {
                distance: 8,
            },
        }),
        useSensor(KeyboardSensor, {
            coordinateGetter: sortableKeyboardCoordinates,
        })
    );

    useEffect(() => {
        fetchGalleryItems();
    }, []);

    const fetchGalleryItems = async () => {
        try {
            const response = await axios.get('/api/gallery/admin/items');
            setItems(response.data);
        } catch (error) {
            console.error('Error fetching gallery items:', error);
            alert('Error fetching gallery items');
        } finally {
            setLoading(false);
        }
    };

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            // Revoke previous preview URL
            if (previewUrl) {
                URL.revokeObjectURL(previewUrl);
            }
            // Create new preview URL
            const newPreviewUrl = URL.createObjectURL(file);
            setPreviewUrl(newPreviewUrl);
            setUploadForm({ ...uploadForm, file });
        }
    };

    const handleUpload = async (e) => {
        e.preventDefault();

        if (!uploadForm.file || !uploadForm.title) {
            alert('Please provide a title and select a file');
            return;
        }

        setUploading(true);

        try {
            const formData = new FormData();
            formData.append('file', uploadForm.file);
            formData.append('title', uploadForm.title);
            formData.append('description', uploadForm.description);
            formData.append('span', uploadForm.span);

            await axios.post('/api/gallery/admin/items', formData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });

            // Reset form and preview
            if (previewUrl) {
                URL.revokeObjectURL(previewUrl);
                setPreviewUrl(null);
            }
            setUploadForm({
                title: '',
                description: '',
                span: 'md:col-span-1 md:row-span-2 sm:col-span-1 sm:row-span-2',
                file: null,
            });
            setShowUploadForm(false);

            // Refresh list
            await fetchGalleryItems();
            alert('Gallery item uploaded successfully!');
        } catch (error) {
            console.error('Error uploading:', error);
            alert('Error uploading gallery item');
        } finally {
            setUploading(false);
        }
    };

    const handleUpdateItem = async (itemId, updates) => {
        try {
            await axios.put(`/api/gallery/admin/items/${itemId}`, updates);
            await fetchGalleryItems();
            setEditingItem(null);
            alert('Item updated successfully!');
        } catch (error) {
            console.error('Error updating item:', error);
            alert('Error updating item');
        }
    };

    const handleDeleteItem = async (itemId) => {
        if (!window.confirm('Are you sure you want to delete this item?')) {
            return;
        }

        try {
            await axios.delete(`/api/gallery/admin/items/${itemId}`);
            await fetchGalleryItems();
            alert('Item deleted successfully!');
        } catch (error) {
            console.error('Error deleting item:', error);
            alert('Error deleting item');
        }
    };

    const toggleActive = async (item) => {
        await handleUpdateItem(item._id, { isActive: !item.isActive });
    };

    // Handle drag end - reorder items
    const handleDragEnd = async (event) => {
        const { active, over } = event;

        if (active.id !== over?.id) {
            const oldIndex = items.findIndex((item) => item._id === active.id);
            const newIndex = items.findIndex((item) => item._id === over.id);

            const newItems = arrayMove(items, oldIndex, newIndex);
            setItems(newItems);

            // Save new order to backend
            setSavingOrder(true);
            try {
                const orderUpdates = newItems.map((item, index) => ({
                    id: item._id,
                    order: index,
                }));

                await axios.put('/api/gallery/admin/items/reorder/bulk', { items: orderUpdates });
            } catch (error) {
                console.error('Error saving order:', error);
                alert('Error saving order. Please try again.');
                // Refresh to get the correct order
                await fetchGalleryItems();
            } finally {
                setSavingOrder(false);
            }
        }
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center p-8">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
            </div>
        );
    }

    return (
        <div className="p-6">
            <div className="flex justify-between items-center mb-6">
                <h1 className="text-2xl font-bold text-gray-800">Gallery Management</h1>
                <button
                    onClick={() => setShowUploadForm(!showUploadForm)}
                    className="bg-blue-500 text-white px-4 py-2 rounded-lg hover:bg-blue-600 flex items-center gap-2"
                >
                    <Upload className="w-4 h-4" />
                    {showUploadForm ? 'Cancel' : 'Upload New Item'}
                </button>
            </div>

            {/* Upload Form */}
            {showUploadForm && (
                <div className="bg-white rounded-lg shadow-md p-6 mb-6">
                    <h2 className="text-xl font-semibold mb-4">Upload New Gallery Item</h2>
                    <form onSubmit={handleUpload}>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Title *
                                </label>
                                <input
                                    type="text"
                                    value={uploadForm.title}
                                    onChange={(e) => setUploadForm({ ...uploadForm, title: e.target.value })}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                                    required
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Size
                                </label>
                                <select
                                    value={uploadForm.span}
                                    onChange={(e) => setUploadForm({ ...uploadForm, span: e.target.value })}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                                >
                                    {spanOptions.map(option => (
                                        <option key={option.value} value={option.value}>
                                            {option.label}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        </div>

                        <div className="mb-4">
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Description
                            </label>
                            <textarea
                                value={uploadForm.description}
                                onChange={(e) => setUploadForm({ ...uploadForm, description: e.target.value })}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                                rows="3"
                            />
                        </div>

                        <div className="mb-4">
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                File (Image or Video) *
                            </label>
                            <input
                                type="file"
                                onChange={handleFileChange}
                                accept="image/*,video/*"
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                                required
                            />
                            {uploadForm.file && previewUrl && (
                                <div className="mt-3">
                                    <div className="flex items-center gap-2 mb-2">
                                        {uploadForm.file.type.startsWith('video/') ? (
                                            <Video className="w-4 h-4 text-blue-500" />
                                        ) : (
                                            <ImageIcon className="w-4 h-4 text-green-500" />
                                        )}
                                        <span className="text-sm text-gray-600">
                                            {uploadForm.file.name}
                                        </span>
                                        <span className="text-xs px-2 py-1 bg-gray-100 rounded">
                                            {uploadForm.file.type.startsWith('video/') ? 'Video' : 'Image'}
                                        </span>
                                        <span className="text-xs text-gray-400">
                                            ({(uploadForm.file.size / (1024 * 1024)).toFixed(2)} MB)
                                        </span>
                                    </div>
                                    {/* File Preview */}
                                    <div className="w-48 h-32 rounded-lg overflow-hidden bg-gray-100 border border-gray-200">
                                        {uploadForm.file.type.startsWith('video/') ? (
                                            <video
                                                src={previewUrl}
                                                className="w-full h-full object-cover"
                                                controls
                                            />
                                        ) : (
                                            <img
                                                src={previewUrl}
                                                alt="Preview"
                                                className="w-full h-full object-cover"
                                            />
                                        )}
                                    </div>
                                </div>
                            )}
                        </div>

                        <button
                            type="submit"
                            disabled={uploading}
                            className="bg-blue-500 text-white px-6 py-2 rounded-lg hover:bg-blue-600 disabled:bg-gray-400 flex items-center gap-2"
                        >
                            {uploading ? (
                                <>
                                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                                    Uploading...
                                </>
                            ) : (
                                <>
                                    <Upload className="w-4 h-4" />
                                    Upload
                                </>
                            )}
                        </button>
                    </form>
                </div>
            )}

            {/* Gallery Items List */}
            <div className="bg-white rounded-lg shadow-md">
                <div className="p-4 border-b flex items-center justify-between">
                    <h2 className="text-lg font-semibold">Gallery Items ({items.length})</h2>
                    {savingOrder && (
                        <div className="flex items-center gap-2 text-sm text-blue-600">
                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                            Saving order...
                        </div>
                    )}
                    {items.length > 0 && !savingOrder && (
                        <p className="text-sm text-gray-500">
                            <GripVertical className="w-4 h-4 inline-block mr-1" />
                            Drag items to reorder
                        </p>
                    )}
                </div>

                <div className="divide-y">
                    {items.length === 0 ? (
                        <div className="p-8 text-center text-gray-500">
                            No gallery items yet. Upload your first item above!
                        </div>
                    ) : (
                        <DndContext
                            sensors={sensors}
                            collisionDetection={closestCenter}
                            onDragEnd={handleDragEnd}
                        >
                            <SortableContext
                                items={items.map(item => item._id)}
                                strategy={verticalListSortingStrategy}
                            >
                                {items.map((item) => (
                                    <SortableGalleryItem
                                        key={item._id}
                                        item={item}
                                        editingItem={editingItem}
                                        setEditingItem={setEditingItem}
                                        spanOptions={spanOptions}
                                        handleUpdateItem={handleUpdateItem}
                                        handleDeleteItem={handleDeleteItem}
                                        toggleActive={toggleActive}
                                    />
                                ))}
                            </SortableContext>
                        </DndContext>
                    )}
                </div>
            </div>
        </div>
    );
};

export default GalleryManagement;
